import { db, FIREBASE_API_KEY } from '../firebase'
import {
  ref,
  query,
  orderByChild,
  orderByKey,
  limitToFirst,
  startAfter,
  equalTo,
  onValue,
  update,
  get,
  remove,
  set,
} from 'firebase/database'
import type { Child, Parent, ScreenLock, AppInfo, BillingInfo, WebFilter, PaymentGatewayConfig, PaymentMethod, Routines, UninstallRequest, DailyQuota, UpdateConfig, UpdateTarget } from './types'
import { nowIso, addDays } from './billing'
import { PLAN_PRICES } from './currency'

const childAppsRef = (childUid: string) => ref(db, `users/childs/${childUid}/apps`)

export interface ChildCredentials {
  childUid: string
  email: string
  password: string
  configJson: string
}

export function subscribeParent(uid: string, cb: (p: Parent | null) => void) {
  return onValue(ref(db, `users/parents/${uid}`), (snap) => {
    cb(snap.exists() ? (snap.val() as Parent) : null)
  })
}

export function subscribeIsAdmin(uid: string, cb: (isAdmin: boolean) => void) {
  return onValue(ref(db, `users/admins/${uid}`), (snap) => cb(snap.exists()))
}

// ---------- Gestión de administradores (Solo SuperAdmin) ----------

export interface AdminEntry {
  uid: string
  email?: string
  name?: string
  addedAt?: string
}

export function subscribeAllAdmins(cb: (admins: AdminEntry[]) => void) {
  return onValue(ref(db, 'users/admins'), (snap) => {
    const out: AdminEntry[] = []
    snap.forEach((child) => {
      out.push({ uid: child.key as string, ...(child.val() as Omit<AdminEntry, 'uid'>) })
    })
    cb(out)
  })
}

export async function removeAdmin(uid: string) {
  await remove(ref(db, `users/admins/${uid}`))
}

// Auto-registrar email/nombre del admin actual en users/admins/{uid} (trazabilidad)
export async function ensureAdminMetadata(uid: string, email: string | null, name?: string | null) {
  const snap = await get(ref(db, `users/admins/${uid}`))
  // El nodo puede ser `true` (flag simple) o un objeto con metadatos
  const raw = snap.exists() ? snap.val() : null
  const cur: Partial<AdminEntry> = raw && typeof raw === 'object' ? (raw as Partial<AdminEntry>) : {}
  const patch: Partial<AdminEntry> = {}
  if (!cur.email && email) patch.email = email
  if (!cur.name && name) patch.name = name
  if (!cur.addedAt) patch.addedAt = new Date().toISOString()
  if (Object.keys(patch).length > 0) await update(ref(db, `users/admins/${uid}`), patch)
}

export function subscribeAllParents(cb: (parents: { uid: string; data: Parent }[]) => void) {
  return onValue(ref(db, 'users/parents'), (snap) => {
    const out: { uid: string; data: Parent }[] = []
    snap.forEach((child) => {
      out.push({ uid: child.key as string, data: child.val() as Parent })
    })
    cb(out)
  })
}

export function subscribeAllChildren(cb: (children: { uid: string; data: Child }[]) => void) {
  return onValue(ref(db, 'users/childs'), (snap) => {
    const out: { uid: string; data: Child }[] = []
    snap.forEach((child) => {
      out.push({ uid: child.key as string, data: child.val() as Child })
    })
    cb(out)
  })
}

// ---------- Paginación por keys para el panel Admin (escala sin Functions) ----------
// Las suscripciones globales descargan TODA la base en cada cambio (telemetría
// de hijos incluida). Estas lecturas puntuales traen páginas de N y el Admin
// pide más con "Cargar más". Tiempo real solo para paymentRequests.

export const ADMIN_PAGE_SIZE = 50

export interface KeyPage<T> {
  rows: { uid: string; data: T }[]
  lastKey: string | null
  hasMore: boolean
}

async function fetchKeyPage<T>(path: 'users/parents' | 'users/childs', pageSize: number, startAfterKey?: string | null): Promise<KeyPage<T>> {
  const q =
    startAfterKey != null
      ? query(ref(db, path), orderByKey(), startAfter(startAfterKey), limitToFirst(pageSize + 1))
      : query(ref(db, path), orderByKey(), limitToFirst(pageSize + 1))
  const snap = await get(q)
  const rows: { uid: string; data: T }[] = []
  snap.forEach((child) => {
    rows.push({ uid: child.key as string, data: child.val() as T })
  })
  const hasMore = rows.length > pageSize
  const page = hasMore ? rows.slice(0, pageSize) : rows
  return { rows: page, lastKey: page.length > 0 ? page[page.length - 1].uid : null, hasMore }
}

export function fetchParentsPage(pageSize = ADMIN_PAGE_SIZE, startAfterKey?: string | null) {
  return fetchKeyPage<Parent>('users/parents', pageSize, startAfterKey)
}

export function fetchChildrenPage(pageSize = ADMIN_PAGE_SIZE, startAfterKey?: string | null) {
  return fetchKeyPage<Child>('users/childs', pageSize, startAfterKey)
}

export async function createParentAccount(name: string, email: string) {
  const password = Math.random().toString(36).slice(2, 10) + 'A1!'
  const resp = await fetch(
    `https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${FIREBASE_API_KEY}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, returnSecureToken: true }),
    },
  )
  const data = await resp.json()
  if (!resp.ok) {
    const msg = data.error?.message ?? 'No se pudo crear la cuenta'
    throw new Error(msg)
  }
  const uid = data.localId as string
  const now = Date.now()
  const billing: BillingInfo = {
    plan: 'trial',
    status: 'active',
    trialStart: new Date(now).toISOString(),
    trialEnd: new Date(now + 14 * 86400000).toISOString(),
  }
  await set(ref(db, `users/parents/${uid}`), {
    name,
    email: email.toLowerCase(),
    billing,
    createdAt: nowIso(),
  })
  return { uid, password }
}

export async function setParentName(uid: string, name: string) {
  await update(ref(db, `users/parents/${uid}`), { name })
}

export async function updateParentBilling(uid: string, billing: BillingInfo) {
  await update(ref(db, `users/parents/${uid}/billing`), billing)
}

export async function startTrial(uid: string) {
  const billing: BillingInfo = {
    plan: 'trial',
    status: 'active',
    trialStart: nowIso(),
    trialEnd: addDays(14),
  }
  await updateParentBilling(uid, billing)
}

export async function activateMonthly(
  uid: string,
  tier: 'premium' | 'familia' = 'familia',
  months = 1,
) {
  const billing: BillingInfo = {
    plan: 'monthly',
    planTier: tier,
    status: 'active',
    activeUntil: addDays(30 * months),
    pricePerMonth: PLAN_PRICES[tier].COP,
    currency: 'COP',
  }
  await updateParentBilling(uid, billing)
}

export async function suspendParent(uid: string) {
  await updateParentBilling(uid, {
    plan: 'trial',
    status: 'suspended',
    trialStart: nowIso(),
    trialEnd: addDays(14),
  })
}

export async function deleteParent(uid: string, email: string) {
  try {
    // Borrar al padre
    await remove(ref(db, `users/parents/${uid}`))
    // Buscar y borrar todos los niños asociados a este email
    // El email se guarda en minúsculas en createParentAccount, así que lo normalizamos
    const emailLower = email.toLowerCase()
    const children = await get(
      query(ref(db, 'users/childs'), orderByChild('parentEmail'), equalTo(emailLower)),
    )
    children.forEach((child) => {
      remove(ref(db, `users/childs/${child.key}`))
    })
    console.log(`✅ Padre ${uid} y sus dispositivos eliminados`)
  } catch (e: unknown) {
    console.error(`❌ Error eliminando padre ${uid}:`, e)
    throw e
  }
}

export function subscribeChildrenByParentEmail(
  parentEmail: string,
  cb: (children: { uid: string; data: Child }[]) => void,
) {
  const q = query(
    ref(db, 'users/childs'),
    orderByChild('parentEmail'),
    equalTo(parentEmail),
  )
  return onValue(q, (snap) => {
    const out: { uid: string; data: Child }[] = []
    snap.forEach((child) => {
      out.push({ uid: child.key as string, data: child.val() as Child })
    })
    cb(out)
  })
}

export function subscribeParentChildren(
  parentUid: string,
  cb: (children: { uid: string; data: Child }[]) => void,
) {
  const idxRef = ref(db, `users/parents/${parentUid}/childs`)
  let childUnsubs: (() => void)[] = []

  const idxUnsub = onValue(idxRef, (snap) => {
    childUnsubs.forEach((u) => u())
    childUnsubs = []
    const uids: string[] = []
    if (snap.exists()) {
      snap.forEach((s) => {
        uids.push(s.key as string)
      })
    }
    if (uids.length === 0) {
      cb([])
      return
    }
    const out: (Child | null)[] = new Array(uids.length).fill(null)
    uids.forEach((childUid, i) => {
      childUnsubs.push(
        onValue(ref(db, `users/childs/${childUid}`), (cs) => {
          out[i] = cs.exists() ? (cs.val() as Child) : null
          const list = uids
            .map((uid, idx) => (out[idx] ? { uid, data: out[idx]! } : null))
            .filter(Boolean) as { uid: string; data: Child }[]
          cb(list)
        }),
      )
    })
  })

  return () => {
    idxUnsub()
    childUnsubs.forEach((u) => u())
  }
}

export async function linkChildToParent(parentUid: string, childUid: string) {
  await set(ref(db, `users/parents/${parentUid}/childs/${childUid}`), true)
}

export async function assignChildToParent(
  childUid: string,
  parentUid: string,
  parentEmail: string,
) {
  await update(ref(db, `users/childs/${childUid}`), { parentEmail })
  await set(ref(db, `users/parents/${parentUid}/childs/${childUid}`), true)
}

export async function unlinkChildFromParent(parentUid: string, childUid: string) {
  // Vínculo bilateral: limpiar parentEmail en el hijo ADEMÁS del índice.
  // Antes solo se borraba el índice y el Admin seguía mostrando el
  // dispositivo como vinculado (filtra por parentEmail). Orden: primero el
  // hijo (aún vinculado → permitido), luego el índice (nodo propio).
  await update(ref(db, `users/childs/${childUid}`), { parentEmail: null })
  await remove(ref(db, `users/parents/${parentUid}/childs/${childUid}`))
}

export async function deleteChild(childUid: string, parentUid?: string) {
  // 1. Eliminar el registro del niño/dispositivo
  await remove(ref(db, `users/childs/${childUid}`))
  // 2. Si se proporciona parentUid, también quitar el enlace del padre
  if (parentUid) {
    await remove(ref(db, `users/parents/${parentUid}/childs/${childUid}`))
  }
}

export function subscribeChild(uid: string, cb: (child: Child | null) => void) {
  return onValue(ref(db, `users/childs/${uid}`), (snap) => {
    cb(snap.exists() ? (snap.val() as Child) : null)
  })
}

export async function setChildAppBlocked(childUid: string, app: AppInfo, blocked: boolean) {
  const cur = await get(childAppsRef(childUid))
  // Normalizar: el nodo puede ser array (agente Android) u objeto con push-keys
  // (versiones anteriores del panel). Object.values lo unifica y además cura
  // nodos ya corrompidos al reescribir como array.
  const raw = cur.exists() ? cur.val() : []
  const apps: AppInfo[] = (Array.isArray(raw) ? raw : Object.values(raw as Record<string, AppInfo>))
    .filter((a) => a && typeof a.packageName === 'string')
  const idx = apps.findIndex((a) => a.packageName === app.packageName)
  if (idx >= 0) {
    apps[idx] = { ...apps[idx], blocked }
  } else {
    apps.push({ ...app, blocked })
  }
  await set(childAppsRef(childUid), apps)
}

export async function updateScreenLock(childUid: string, lock: ScreenLock) {
  await update(ref(db, `users/childs/${childUid}/screenLock`), lock)
}

export async function updateWebFilter(childUid: string, webFilter: WebFilter) {
  await set(ref(db, `users/childs/${childUid}/webFilter`), webFilter)
}

// ---------- Configuración de pagos (SuperAdmin) ----------

export function subscribePaymentConfig(cb: (config: PaymentGatewayConfig | null) => void) {
  return onValue(ref(db, 'config/payments'), (snap) => {
    cb(snap.exists() ? (snap.val() as PaymentGatewayConfig) : null)
  })
}

export async function updatePaymentConfig(config: PaymentGatewayConfig) {
  await set(ref(db, 'config/payments'), config)
}

// ---------- Solicitudes de activación de plan ----------

export interface PaymentRequest {
  uid: string
  name: string
  email: string
  requestedAt: string
  status: string
  plan?: string
  method?: PaymentMethod
  receiptImage?: string
  amountPaid?: number
}

export async function requestPlanActivation(
  uid: string,
  name: string,
  email: string,
  plan: string,
  method: PaymentMethod,
  receiptImage?: string,
  amountPaid?: number,
) {
  await set(ref(db, `users/paymentRequests/${uid}`), {
    uid,
    name,
    email: email.toLowerCase(),
    requestedAt: nowIso(),
    status: 'pending',
    plan,
    method,
    receiptImage: receiptImage ?? '',
    amountPaid: amountPaid ?? null,
  })
}

export function subscribePaymentRequests(cb: (reqs: PaymentRequest[]) => void) {
  return onValue(ref(db, 'users/paymentRequests'), (snap) => {
    const out: PaymentRequest[] = []
    snap.forEach((r) => {
      const data = r.val() as PaymentRequest
      if (data && data.status === 'pending') out.push(data)
    })
    cb(out)
  })
}

export async function resolvePaymentRequest(uid: string) {
  await remove(ref(db, `users/paymentRequests/${uid}`))
}

// ---------- Crear perfil de hijo (independiente del dispositivo) ----------
// Cada hijo es un perfil independiente. El agente (Windows/Android) se elige al descargar el instalador.

export async function createChildProfile(
  parentUid: string,
  parentEmail: string,
  childName: string,
): Promise<ChildCredentials> {
  // Generar email único para el hijo
  const safeName = childName.toLowerCase().replace(/[^a-z0-9]/g, '')
  const randomSuffix = Math.random().toString(36).slice(2, 8)
  const email = `kidsafe.${safeName}.${randomSuffix}@kid.local`
  const password = Math.random().toString(36).slice(2, 12) + 'A1!'

  // 1. Crear cuenta en Firebase Auth
  const resp = await fetch(
    `https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${FIREBASE_API_KEY}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, returnSecureToken: true }),
    },
  )
  const authData = await resp.json()
  if (!resp.ok) {
    const msg = authData.error?.message ?? 'No se pudo crear la cuenta del hijo'
    throw new Error(msg)
  }
  const childUid = authData.localId as string

  // 2. Crear registro en RTDB (perfil independiente, sin deviceType fijo)
  const childData: Child = {
    name: childName,
    email,
    parentEmail: parentEmail.toLowerCase(),
    apps: [],
    screenLock: { hours: 2, minutes: 0, locked: false },
    webFilter: {
      enabled: true,
      ageBand: 'under13',
      categories: {
        adult: true,
        gambling: true,
        drugs: true,
        social: false,
        violence: true,
      },
      customDomains: [],
    },
    location: {
      geoFence: false,
    },
  }
  await set(ref(db, `users/childs/${childUid}`), childData)

  // 3. Vincular al padre
  await set(ref(db, `users/parents/${parentUid}/childs/${childUid}`), true)
  // Guardar credenciales para regenerar instalador después (sin exponer password en Child)
  await set(ref(db, `users/parents/${parentUid}/childCredentials/${childUid}`), { email, password })

  // 4. Generar config.json para el instalador
  const config = {
    apiKey: FIREBASE_API_KEY,
    databaseUrl: 'https://chicoseguro-9e6c1-default-rtdb.firebaseio.com',
    email,
    password,
    parentEmail: parentEmail.toLowerCase(),
    deviceName: childName,
  }

  return {
    childUid,
    email,
    password,
    configJson: JSON.stringify(config, null, 2),
  }
}

export async function getInstallerConfigForChild(parentUid: string, childUid: string): Promise<ChildCredentials> {
  const [childSnap, credSnap] = await Promise.all([
    get(ref(db, `users/childs/${childUid}`)),
    get(ref(db, `users/parents/${parentUid}/childCredentials/${childUid}`)),
  ])
  if (!childSnap.exists()) throw new Error('Perfil de hijo no encontrado')
  const child = childSnap.val() as Child
  const cred = credSnap.val() as { email?: string; password?: string } | null
  if (!cred?.password) throw new Error('Credenciales no encontradas. Crea de nuevo el perfil o contacta soporte.')
  const email = cred.email ?? child.email ?? ''
  const password = cred.password
  const parentEmail = child.parentEmail ?? ''
  const config = {
    apiKey: FIREBASE_API_KEY,
    databaseUrl: 'https://chicoseguro-9e6c1-default-rtdb.firebaseio.com',
    email,
    password,
    parentEmail: parentEmail.toLowerCase(),
    deviceName: child.name ?? 'Dispositivo',
  }
  return { childUid, email, password, configJson: JSON.stringify(config, null, 2) }
}

// ---------- Rutinas (límites por días/horas) ----------

export async function updateRoutines(childUid: string, routines: Routines) {
  await set(ref(db, `users/childs/${childUid}/routines`), routines)
}

export function subscribeRoutines(childUid: string, cb: (r: Routines | null) => void) {
  return onValue(ref(db, `users/childs/${childUid}/routines`), (snap) => cb(snap.exists() ? (snap.val() as Routines) : null))
}

export async function updateDailyQuota(childUid: string, quota: DailyQuota) {
  await set(ref(db, `users/childs/${childUid}/dailyQuota`), quota)
}

export function subscribeDailyQuota(childUid: string, cb: (q: DailyQuota | null) => void) {
  return onValue(ref(db, `users/childs/${childUid}/dailyQuota`), (snap) => cb(snap.exists() ? (snap.val() as DailyQuota) : null))
}

// ---------- Desinstalación con clave ----------

export async function requestUninstallCode(childUid: string, parentUid: string): Promise<string> {
  const code = Math.floor(100000 + Math.random() * 900000).toString()
  const expiresAt = new Date(Date.now() + 10 * 60 * 1000).toISOString()
  const req: UninstallRequest = { code, expiresAt, by: parentUid, createdAt: nowIso() }
  await set(ref(db, `users/childs/${childUid}/uninstallRequest`), req)
  return code
}

export function subscribeUninstallRequest(childUid: string, cb: (r: UninstallRequest | null) => void) {
  return onValue(ref(db, `users/childs/${childUid}/uninstallRequest`), (snap) => cb(snap.exists() ? (snap.val() as UninstallRequest) : null))
}

export async function clearUninstallRequest(childUid: string) {
  await remove(ref(db, `users/childs/${childUid}/uninstallRequest`))
}

export async function clearUnlockRequest(childUid: string) {
  await remove(ref(db, `users/childs/${childUid}/unlockRequest`))
}

// ---------- Updates push SuperAdmin ----------

export function subscribeUpdateConfig(cb: (c: UpdateConfig | null) => void) {
  return onValue(ref(db, 'config/updates'), (snap) => cb(snap.exists() ? (snap.val() as UpdateConfig) : null))
}

export async function publishUpdateConfig(target: UpdateTarget, notes: string, windowsVersion: string, androidVersion: string, adminUid: string) {
  const cfg: UpdateConfig = {
    windowsVersion,
    androidVersion,
    target,
    notes: notes.trim() || `Actualización ${target} ${windowsVersion}/${androidVersion}`,
    updatedAt: nowIso(),
    updatedBy: adminUid,
    force: true,
  }
  await set(ref(db, 'config/updates'), cfg)
}
